/**
 * FormaFilesScreen — Forma "Data Management → Files" (#/forma).
 *
 * Live ACC browse only (hub → project → folders → .rvt / submission files).
 */

import type { ComponentChildren } from "preact";
import { isAuthDisabled } from "../../auth/apsConfig";
import { useAccBrowse } from "../../hooks/useAccBrowse";
import type { AccFileSelection } from "../../stores/accBrowseStore";
import { AccHubEmptyHelp } from "../AccHubEmptyHelp";
import { BrowseBusyOverlay } from "../BrowseBusyOverlay";
import { Fc2RefreshButton } from "../Fc2RefreshButton";
import { FormaLeftRail } from "./FormaLeftRail";
import { FORMA } from "./FormaTokens";
import { FormaTopBar } from "./FormaTopBar";

const FC2_ACCENT = "#6E56CF";

export function FormaFilesScreen({
  onOpenFc2,
}: {
  onOpenFc2: (selection: AccFileSelection) => void;
}) {
  if (isAuthDisabled()) {
    return (
      <FormaShell>
        <p
          style={{
            fontSize: 13,
            color: FORMA.textSecondary,
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          Live ACC file browse requires Autodesk sign-in. Set{" "}
          <code style={{ fontSize: 12 }}>VITE_ENABLE_APS_AUTH=true</code> in the
          repo-root <code style={{ fontSize: 12 }}>.env</code> and restart{" "}
          <code style={{ fontSize: 12 }}>pnpm dev</code>.
        </p>
      </FormaShell>
    );
  }

  return <LiveAccFormaFilesScreen onOpenFc2={onOpenFc2} />;
}

function LiveAccFormaFilesScreen({
  onOpenFc2,
}: {
  onOpenFc2: (selection: AccFileSelection) => void;
}) {
  const browse = useAccBrowse();

  const openFc2 = () => {
    if (browse.selection) onOpenFc2(browse.selection);
  };

  return (
    <FormaShell>
      <HubProjectBar
        hubs={browse.hubs}
        projects={browse.projects}
        hubId={browse.hub?.id ?? ""}
        projectId={browse.project?.id ?? ""}
        region={browse.hub?.region}
        onHubChange={id => void browse.selectHub(id)}
        onProjectChange={id => void browse.selectProject(id)}
        loadingHubs={browse.loadingKind === "hubs"}
        loadingFiles={browse.loadingKind === "files"}
        onRefresh={() => void browse.refreshHubsAndProjects()}
        onRefreshFiles={() => void browse.refreshCurrentFolder()}
      />

      {browse.error && (
        <p style={{ color: "#c0392b", margin: "0 0 12px", fontSize: 12 }}>
          {browse.error}
        </p>
      )}

      {!browse.loading && !browse.error && browse.hubs.length === 0 && (
        <div
          style={{
            marginBottom: 12,
            padding: 12,
            borderRadius: 6,
            border: `1px solid ${FORMA.borderSoft}`,
            background: "#fff",
          }}
        >
          <AccHubEmptyHelp />
        </div>
      )}

      <div style={{ display: "flex", gap: 18, flex: 1, minHeight: 0 }}>
        <div style={{ width: 210, flexShrink: 0, overflowY: "auto" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: FORMA.textSecondary,
              marginBottom: 8,
              textTransform: "uppercase",
            }}
          >
            Folders
          </div>
          {browse.crumbs.length > 0 && (
            <div style={{ marginBottom: 8, fontSize: 11, lineHeight: 1.5 }}>
              {browse.crumbs.map((c, i) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => void browse.navigateToCrumb(i)}
                  style={{
                    display: "block",
                    background: "none",
                    border: "none",
                    padding: "2px 0",
                    font: "inherit",
                    fontSize: 11,
                    color: FORMA.blue,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {i > 0 ? "↳ " : ""}
                  {c.name}
                </button>
              ))}
            </div>
          )}
          {browse.sidebarFolders.map(f => (
            <button
              type="button"
              key={f.id}
              onClick={() => void browse.openSidebarFolder(f)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "8px 10px",
                marginBottom: 2,
                borderRadius: 4,
                border: "none",
                cursor: "pointer",
                font: "inherit",
                fontSize: 13,
                textAlign: "left",
                background:
                  browse.activeFolderId === f.id
                    ? FORMA.blueSoftBg
                    : "transparent",
                color:
                  browse.activeFolderId === f.id
                    ? FORMA.blue
                    : FORMA.textPrimary,
                fontWeight: 500,
              }}
            >
              <span aria-hidden>🗀</span>
              {f.name}
            </button>
          ))}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            minHeight: 160,
          }}
        >
          {browse.loadingKind === "files" ? (
            <BrowseBusyOverlay message="Refreshing…" variant="light" />
          ) : null}
          <Toolbar fc2Enabled={!!browse.selection} onOpenFc2={openFc2} />
          <LiveFileTable
            rows={browse.fileRows}
            selectedId={browse.selectedItemId}
            hubName={browse.hub?.name ?? ""}
            onSelect={id => browse.selectFile(id)}
          />
          <FooterCount
            selected={!!browse.selectedItemId}
            total={browse.fileRows.length}
          />
        </div>
      </div>
    </FormaShell>
  );
}

function FormaShell({ children }: { children: ComponentChildren }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        background: FORMA.pageBg,
        color: FORMA.textPrimary,
        fontSize: 13,
      }}
    >
      <FormaTopBar />
      <main style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <FormaLeftRail />
        <section
          aria-label="Files"
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            background: "#fff",
            padding: "20px 28px",
            overflow: "auto",
          }}
        >
          <h1 style={{ fontSize: 24, fontWeight: 600, margin: "0 0 16px" }}>
            Files
          </h1>
          <div
            style={{
              display: "flex",
              gap: 22,
              borderBottom: `1px solid ${FORMA.borderSoft}`,
              marginBottom: 16,
            }}
          >
            <Tab label="Folders" active />
            <Tab label="Packages" />
          </div>
          {children}
        </section>
      </main>
    </div>
  );
}

function HubProjectBar({
  hubs,
  projects,
  hubId,
  projectId,
  region,
  onHubChange,
  onProjectChange,
  onRefresh,
  onRefreshFiles,
  loadingHubs,
  loadingFiles,
}: {
  hubs: { id: string; name: string }[];
  projects: { id: string; name: string }[];
  hubId: string;
  projectId: string;
  region?: string;
  loadingHubs: boolean;
  loadingFiles: boolean;
  onHubChange: (id: string) => void;
  onProjectChange: (id: string) => void;
  onRefresh: () => void;
  onRefreshFiles: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        alignItems: "center",
        marginBottom: 14,
        fontSize: 12,
      }}
    >
      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        Hub
        <select
          value={hubId}
          onChange={e => onHubChange((e.target as HTMLSelectElement).value)}
          style={selectStyle}
        >
          {hubs.map(h => (
            <option key={h.id} value={h.id}>
              {h.name}
            </option>
          ))}
        </select>
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
        Project
        <select
          value={projectId}
          onChange={e => onProjectChange((e.target as HTMLSelectElement).value)}
          style={selectStyle}
        >
          {projects.map(p => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      {region && (
        <span style={{ color: FORMA.textSecondary }}>Region: {region}</span>
      )}
      <Fc2RefreshButton
        loading={loadingHubs}
        style={ghostBtn}
        title="Reload hub and project lists"
        onClick={onRefresh}
      >
        Refresh
      </Fc2RefreshButton>
      {projectId ? (
        <Fc2RefreshButton
          loading={loadingFiles}
          style={ghostBtn}
          title="Reload files in the current folder"
          onClick={onRefreshFiles}
        >
          Refresh files
        </Fc2RefreshButton>
      ) : null}
    </div>
  );
}

function LiveFileTable({
  rows,
  selectedId,
  hubName,
  onSelect,
}: {
  rows: { itemId: string; fileName: string }[];
  selectedId: string | null;
  hubName: string;
  onSelect: (id: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <p style={{ color: FORMA.textSecondary, margin: 0 }}>
        No submission files (.rvt, .pdf) in this folder.
      </p>
    );
  }
  return (
    <div style={{ border: `1px solid ${FORMA.borderSoft}`, borderRadius: 6 }}>
      <div style={{ ...rowGridLive, ...headerRow }}>
        <span />
        <span>Name</span>
        <span>Hub</span>
      </div>
      {rows.map(r => {
        const selected = r.itemId === selectedId;
        return (
          <button
            type="button"
            key={r.itemId}
            onClick={() => onSelect(r.itemId)}
            style={{
              ...rowGridLive,
              ...dataRow,
              background: selected ? FORMA.blueSoftBg : "#fff",
            }}
          >
            <input type="checkbox" checked={selected} readOnly aria-hidden />
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={rvtBadge}>R</span>
              <span style={{ fontWeight: 500, color: FORMA.textPrimary }}>
                {r.fileName}
              </span>
            </span>
            <span style={{ color: FORMA.textSecondary, fontSize: 12 }}>
              {hubName}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function FooterCount({
  selected,
  total,
}: {
  selected: boolean;
  total: number;
}) {
  return (
    <div
      style={{
        marginTop: "auto",
        paddingTop: 12,
        fontSize: 12,
        color: FORMA.textSecondary,
        borderTop: `1px solid ${FORMA.borderSoft}`,
      }}
    >
      {selected ? `1 of ${total} selected` : `${total} items`}
      {selected ? (
        <span style={{ marginLeft: 10, color: FC2_ACCENT }}>
          — click <b>FC2</b> to run code compliance on this model
        </span>
      ) : (
        <span style={{ marginLeft: 10 }}>— select a model to enable FC2</span>
      )}
    </div>
  );
}

function Toolbar({
  fc2Enabled,
  onOpenFc2,
}: {
  fc2Enabled: boolean;
  onOpenFc2: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
      }}
    >
      <button type="button" style={uploadBtn}>
        ⬆ Upload files <span style={{ opacity: 0.85 }}>▾</span>
      </button>
      {fc2Enabled && (
        <button type="button" onClick={onOpenFc2} style={fc2Btn}>
          ✦ Check with FC2
        </button>
      )}
      <button type="button" style={ghostBtn}>
        ⇄ Move
      </button>
      <span style={{ marginLeft: "auto", color: "#9aa3af", fontSize: 12 }}>
        ⌕ Search and filter
      </span>
    </div>
  );
}

function Tab({ label, active }: { label: string; active?: boolean }) {
  return (
    <span
      style={{
        padding: "8px 2px",
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        color: active ? FORMA.textPrimary : FORMA.textSecondary,
        borderBottom: active
          ? `2px solid ${FORMA.blue}`
          : "2px solid transparent",
      }}
    >
      {label}
    </span>
  );
}

const selectStyle = {
  font: "inherit",
  fontSize: 12,
  padding: "6px 8px",
  borderRadius: 4,
  border: `1px solid ${FORMA.borderHard}`,
  minWidth: 160,
} as const;

const uploadBtn = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: FORMA.blue,
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
} as const;

const fc2Btn = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: FC2_ACCENT,
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 1px 6px rgba(110,86,207,0.4)",
} as const;

const ghostBtn = {
  background: "#fff",
  color: FORMA.textPrimary,
  border: `1px solid ${FORMA.borderHard}`,
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  cursor: "pointer",
} as const;

const rowGridLive = {
  display: "grid",
  gridTemplateColumns: "34px 2fr 1fr",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  textAlign: "left",
  width: "100%",
} as const;

const headerRow = {
  borderBottom: `1px solid ${FORMA.borderSoft}`,
  fontSize: 12,
  fontWeight: 600,
  color: FORMA.textSecondary,
  background: "#fafbfc",
} as const;

const dataRow = {
  border: "none",
  borderTop: `1px solid ${FORMA.borderSoft}`,
  cursor: "pointer",
  fontSize: 13,
  fontFamily: "inherit",
} as const;

const rvtBadge = {
  width: 22,
  height: 22,
  borderRadius: 3,
  background: FORMA.blue,
  color: "#fff",
  fontSize: 11,
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
} as const;
